/**
 * Zod input schemas: documents. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
/** Document Vault category management (server-side: protects + cascades). */
export const DocumentCategoryAddSchema = z.object({
    name: z.string().trim().min(1).max(60),
});
export const DocumentCategoryRenameSchema = z.object({
    oldName: z.string().trim().min(1).max(60),
    newName: z.string().trim().min(1).max(60),
});
export const DocumentCategoryDeleteSchema = z.object({
    name: z.string().trim().min(1).max(60),
});
export const DocumentCreateSchema = z.object({
    employeeEmail: z.string().trim().toLowerCase().email(),
    category: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1, 'A title is required').max(200),
    note: z.string().trim().max(1000).optional(),
    storagePath: z.string().trim().min(1).max(500),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    sizeBytes: z.number().int().nonnegative(),
    visibleToEmployee: z.boolean().optional().default(false),
});
// -- Company (Team) documents — HR-published policies with audience segmentation --
export const CompanyDocumentCreateSchema = z.object({
    title: z.string().trim().min(1).max(200),
    category: z.string().trim().min(1).max(80),
    filename: z.string().trim().min(1).max(300),
    storagePath: z.string().trim().min(1).max(500),
    mimeType: z.string().trim().min(1).max(120),
    sizeBytes: z.number().int().min(0),
    note: z.string().trim().max(1000).nullable().optional(),
    entities: z.array(z.string().trim().min(1)).max(30).default([]),
    teams: z.array(z.string().trim().min(1)).max(50).default([]),
});
export const CompanyDocumentUpdateSchema = z.object({
    id: z.string().min(1),
    title: z.string().trim().min(1).max(200).optional(),
    category: z.string().trim().min(1).max(80).optional(),
    note: z.string().trim().max(1000).nullable().optional(),
    entities: z.array(z.string().trim().min(1)).max(30).optional(),
    teams: z.array(z.string().trim().min(1)).max(50).optional(),
});
export const CompanyDocumentDeleteSchema = z.object({ id: z.string().min(1) });
export const CompanyDocumentUrlSchema = z.object({
    id: z.string().min(1),
    download: z.boolean().optional(),
});
export const DocumentClassifySchema = z.object({
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    /** Base64 file content (no data: prefix). Capped ~20 MB encoded. */
    contentBase64: z.string().min(1).max(28_000_000),
});
export const DocumentDeleteSchema = z.object({ id: z.string().min(1) });
export const DocumentSetVisibilitySchema = z.object({
    id: z.string().min(1),
    visible: z.boolean(),
});
export const DocumentSubmissionCreateSchema = z.object({
    /** Must live under documentSubmissions/{caller}/ — server-verified. */
    storagePath: z.string().min(1).max(500),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    sizeBytes: z.number().int().positive().max(25 * 1024 * 1024),
    comment: z.string().trim().max(1000).optional(),
});
export const DocumentSubmissionDecideSchema = z.object({
    id: z.string().min(1),
    decision: z.enum(['approve', 'reject']),
    /** Required for approve (validated server-side against the category list). */
    category: z.string().trim().min(1).max(80).optional(),
    hrComment: z.string().trim().max(1000).optional(),
});
export const DocumentSubmissionUrlSchema = z.object({
    id: z.string().min(1),
    download: z.boolean().optional(),
});
export const DocumentSetCategorySchema = z.object({
    id: z.string().min(1),
    category: z.string().trim().min(1).max(80),
});
/** Hub admin corrects the year (and optional month) a vault document belongs to. */
export const DocumentSetPeriodSchema = z.object({
    id: z.string().min(1),
    year: z.number().int().min(2000).max(2100),
    month: z.number().int().min(1).max(12).nullish(),
});
export const DocumentDownloadUrlSchema = z.object({
    id: z.string().min(1),
    /** True → signed URL forces Content-Disposition: attachment (real download). */
    download: z.boolean().optional(),
});
//# sourceMappingURL=documents.js.map